import {
  Component,
  OnInit,
  signal,
  inject,
  OnDestroy,
  ViewChild,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

import {
  SearchFormComponent,
  SearchFormData,
} from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { PermissionService } from '@app/core/services/permission.service';
import { StockDeliveryService, StockDeliveryResultSearchParams } from '../stock-delivery.service';
import { withPaginatedSearchFields } from '@shared/utils/paginated-field.helpers';
import {
  StockDeliveryResultRecord,
  STOCK_DELIVERY_RESULT_SEARCH_CONFIG,
  STOCK_DELIVERY_RESULT_TABLE_COLUMNS,
  STOCK_DELIVERY_DEFAULT_CONFIG,
  STOCK_DELIVERY_MESSAGES,
  STOCK_DELIVERY_RESULT_DEFAULT_COLUMNS_ORDER,
  STOCK_DELIVERY_RESULT_FLEXIBLE_COLUMNS_ORDER,
  STOCK_DELIVERY_RESULT_ACTION_COLUMN,
} from '../stock-delivery.config';
import { BASE_PATH, OutboundDeliveryOrderControllerService, ResultRecordControllerService } from '@app/api';
import { environment } from '@environments/environment';
import { getOutboundTypeLabel, getWarehouseOrStoreName } from '../delivery.helpers';
import { formatDateTimeWithLocale } from '@app/shared/utils/date-formatter.util';

@Component({
  selector: 'app-stock-delivery-result',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TranslateModule,
    SearchFormComponent,
    AdvancedDataTableComponent,
    AppButtonComponent,
  ],
  providers: [
    MessageService,
    {
      provide: BASE_PATH,
      useValue: environment.stockURL,
    },
    OutboundDeliveryOrderControllerService,
    ResultRecordControllerService,
    StockDeliveryService,
  ],
  templateUrl: './stock-delivery-result.component.html',
  styleUrl: './stock-delivery-result.component.css',
})
export class StockDeliveryResultComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private messageService = inject(MessageService);
  private stockDeliveryService = inject(StockDeliveryService);
  private translateService = inject(TranslateService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private destroy$ = new Subject<void>();

  // Permission flags - computed from PermissionService
  // Using specific submenu 'outbound' for inventory permissions
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.outbound.view');
  });

  // Search form configuration
  searchFormat = signal(
    withPaginatedSearchFields(STOCK_DELIVERY_RESULT_SEARCH_CONFIG, {
      outLogicWarehouseCode: {
        loadPage: (params) =>
          this.stockDeliveryService.getWarehouseOptionsPage(params),
      },
    }),
  );

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      STOCK_DELIVERY_RESULT_TABLE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of STOCK_DELIVERY_RESULT_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) {
        cols.push({
          ...col,
          label: this.translateService.instant(col.label),
        });
      }
    }

    // Conditionally add actions column at the end if permitted
    if (this.canRead()) {
      const actionsCol = fromConfig(
        STOCK_DELIVERY_RESULT_ACTION_COLUMN.ACTIONS
      );
      if (actionsCol) {
        cols.push({
          ...actionsCol,
          label: this.translateService.instant(actionsCol.label),
        });
      }
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      STOCK_DELIVERY_RESULT_TABLE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of STOCK_DELIVERY_RESULT_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push({
          ...col,
          label: this.translateService.instant(col.label),
        });
      }
    }

    return cols;
  });

  // Data signals
  dataSource = signal<StockDeliveryResultRecord[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  searchParams = signal({
    page: 1, // Use 1-based pagination
    size: STOCK_DELIVERY_DEFAULT_CONFIG.PAGE_SIZE,
  });

  // Sorting
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<1 | -1 | undefined>(undefined);

  // Flag to prevent double API calls
  private isSearching = false;

  // Reference to advanced table component
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  // Configuration constants (for template access)
  readonly STOCK_DELIVERY_DEFAULT_CONFIG = STOCK_DELIVERY_DEFAULT_CONFIG;

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearch(searchData: SearchFormData): void {
    if (this.isSearching) return;

    this.isSearching = true;
    this.searchParams.set({ ...this.searchParams(), page: 1 });

    const searchParams: StockDeliveryResultSearchParams = {
      ...searchData,
      page: 0,
      size: this.searchParams().size,
    };

    this.loadData(searchParams);
  }

  onReset(): void {
    this.searchParams.set({
      page: 1,
      size: STOCK_DELIVERY_DEFAULT_CONFIG.PAGE_SIZE,
    });
    this.sortField.set(undefined);
    this.sortOrder.set(undefined);
    this.loadData();
  }

  onPageChange(event: any): void {
    this.searchParams.set({
      ...this.searchParams(),
      page: event.page + 1, // Advanced table uses 0-based, convert to 1-based
      size: event.rows ?? 20,
    });
    this.loadData();
  }

  onSortChange(event: { field: string; order: 1 | -1 }): void {
    this.sortField.set(event.field);
    this.sortOrder.set(event.order);
    this.searchParams.set({ ...this.searchParams(), page: 1 }); // Reset to first page when sorting
    this.loadData();
  }

  // Format creation time (with time)
  formatCreationTime(dateString: string | undefined): string {
    if (!dateString) return '-';
    return formatDateTimeWithLocale(dateString, 'vi-VN') || '-';
  }

  onViewDetails(record: StockDeliveryResultRecord): void {
    if (!this.canRead()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.permissions.no_permission'),
        detail: this.translateService.instant('common.permissions.no_permission'),
      });
      return;
    }

    if (!record || !record.id) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail: this.translateService.instant('outbound_management.messages.failed_to_load'),
      });
      return;
    }

    this.router.navigate(['/stock/delivery/result/view', record.id]);
  }

  // Get outbound type label for display
  getOutboundTypeLabel(outType: string): string {
    return getOutboundTypeLabel(outType, this.translateService);
  }

  // Get warehouse or store name (using helper)
  getWarehouseOrStoreName = getWarehouseOrStoreName;

  // Refresh data
  refreshData(): void {
    this.searchParams.set({ ...this.searchParams(), page: 1 });
    this.loadData();
  }

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }

  private loadData(searchParams?: StockDeliveryResultSearchParams): void {
    this.loading.set(true);

    const params: StockDeliveryResultSearchParams = {
      ...searchParams,
      page:
        searchParams?.page !== undefined
          ? searchParams.page
          : this.searchParams().page - 1, // Convert 1-based to 0-based for API
      size: this.searchParams().size,
      sortBy: this.sortField() || 'creationDate',
      ascending: this.sortOrder() === 1,
    };

    this.stockDeliveryService
      .getDeliveryResultRecords(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.dataSource.set(response.data);
          this.totalRecords.set(response.totalRecords);
          this.loading.set(false);
          this.isSearching = false;
          
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Data loaded successfully',
          });
        },
        error: (error) => {
          console.error('Error loading delivery result records:', error);
          this.loading.set(false);
          this.isSearching = false;
          
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: STOCK_DELIVERY_MESSAGES.ERROR.DATA_LOAD_FAILED,
          });
        },
      });
  }

}
